// MB.7 — villager daily schedule. PURE (ENG-1). Maps the world time-of-day to the
// activity a villager should be doing, matching Minecraft's rough day plan. The
// host reads this to drive pathing (to a jobsite, the meeting bell, or a bed) —
// the A*/persistence that acts on it is the MB.7 remainder; this is the clock.

export const DAY_TICKS = 24_000;

export type Activity = 'wander' | 'work' | 'meet' | 'play' | 'sleep';

export interface VillagerDay {
  /** Has a profession + jobsite (works during the work block). */
  employed: boolean;
  /** A child plays instead of working and never mans a jobsite. */
  child: boolean;
}

/**
 * Activity at a time-of-day (0 = sunrise). The day runs:
 *   [0,2000)    wake, wander near home
 *   [2000,9000) work (employed adult) / play (child) / wander (jobless adult)
 *   [9000,11000) meet — gather at the village bell
 *   [11000,12000) wander / idle before dusk
 *   [12000,24000) sleep
 */
export function activityAt(timeOfDay: number, day: VillagerDay): Activity {
  const t = ((timeOfDay % DAY_TICKS) + DAY_TICKS) % DAY_TICKS;
  if (t >= 12_000) return 'sleep';
  if (t >= 9_000 && t < 11_000) return day.child ? 'play' : 'meet';
  if (t >= 2_000 && t < 9_000) return day.child ? 'play' : day.employed ? 'work' : 'wander';
  return 'wander';
}

/** Whether a villager should be pathing to (and holding) a bed right now. */
export function isSleeping(timeOfDay: number, day: VillagerDay): boolean {
  return activityAt(timeOfDay, day) === 'sleep';
}

/** Which cached point a villager should path toward for an activity: its bed at
 *  night, the meeting bell during the meet block, otherwise home (wander/work/
 *  play all mill about home in this slice — jobsite pathing is a later remainder). */
export type Destination = 'bed' | 'meet' | 'home';
export function scheduleDestination(activity: Activity): Destination {
  if (activity === 'sleep') return 'bed';
  if (activity === 'meet') return 'meet';
  return 'home';
}
